import { useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, Plus, RefreshCw, Search, Trash2, Upload } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useProductGroup } from '../../../../apps/hooks/productGroup/useProductGroup';
import { useProductGroupList } from '../../../../apps/hooks/productGroup/useProductGroupList';
import { collectAttributePath, collectLeafSkus } from '../utils/treeQueries';
import { IdBadge, IdBadgeType } from '../../../../components/ui/badge/IdBadge';
import { ROUTES } from '../../../../routes/routes';
import { convertToLocalTime } from '../../../../utils/dateUtils';
import { CsvUploadModal } from '../components/csv/CsvUploadModal';

export default function ProductGroupListPage() {
  const productGroupClient = useProductGroup();
  const navigate = useNavigate();
  const {
    groups,
    filteredGroups,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    refetch,
    deleteGroup,
  } = useProductGroupList();
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredGroups.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  const copySkuToClipboard = (sku: string) => {
    void navigator.clipboard.writeText(sku).catch(() => undefined);
    toast.success(`Copied SKU ${sku}`);
  };

  return (
    <Container fluid className="py-3">
      <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="h2 mb-1">Product Groups</h4>
          <p className="text-muted mb-0">
            Manage SkinSeoul Products in groups by attributes (color, size, etc.)
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Button variant="outline-dark" size="sm" onClick={() => setCsvModalOpen(true)}>
            <Upload size={16} className="me-1" />
            Upload CSV
          </Button>
          <Button variant="dark" size="sm" onClick={() => navigate(ROUTES.catalog.productGroups.new)}>
            <Plus size={16} className="me-1" />
            New Group
          </Button>
          <Button variant="outline-dark" size="sm" onClick={refetch} disabled={loading} title="Refresh">
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </Button>
        </div>
      </div>

      <CsvUploadModal
        show={csvModalOpen}
        onHide={() => {
          setCsvModalOpen(false);
          void refetch();
        }}
        onUpload={(uploadGroups) => productGroupClient.bulkCreate(uploadGroups)}
      />

      <Card className="mb-3">
        <Card.Body>
          <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
          <InputGroup size="sm">
            <InputGroup.Text><Search size={14} /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Filter by group name or member SKU"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </InputGroup>
          {loading && (
            <div className="text-muted fst-italic mt-1" style={{ fontSize: '0.7rem' }}>
              Loading..
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}

          {loading && groups.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="dark" className="mb-3" />
              <p className="text-muted mb-0">Loading product groups…</p>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="mb-1">No product groups found</h5>
              <p className="text-muted mb-0">
                {searchQuery ? 'Try clearing the search.' : 'Create the first one to get started.'}
              </p>
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className="table-responsive"
              style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}
            >
              <Table hover size="sm" className="mb-0 align-middle">
                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '28%', fontSize: '0.8rem', fontWeight: 600 }}>Name</th>
                    <th style={{ width: '180px', fontSize: '0.8rem', fontWeight: 600 }}>Attributes</th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 600 }}>Member SKUs</th>
                    <th style={{ width: '160px', fontSize: '0.8rem', fontWeight: 600 }}>Last updated</th>
                    <th style={{ width: '100px', fontSize: '0.8rem', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const virtualRows = rowVirtualizer.getVirtualItems();
                    const paddingTop = virtualRows[0]?.start ?? 0;
                    const paddingBottom = rowVirtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0);
                    return (
                      <>
                        {paddingTop > 0 && (
                          <tr style={{ height: paddingTop }}><td colSpan={5} /></tr>
                        )}
                        {virtualRows.map((virtualRow) => {
                          const group = filteredGroups[virtualRow.index]!;
                          const attributePath = collectAttributePath(group);
                          const memberSkus = collectLeafSkus(group.members);
                          return (
                      <tr key={group.groupId}>
                        <td style={{ padding: '8px 6px' }}>
                          <Link
                            to={ROUTES.catalog.productGroups.edit(group.groupId)}
                            className="fw-semibold text-decoration-none"
                          >
                            {group.groupName}
                          </Link>
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          <div className="d-flex flex-wrap gap-1">
                            {attributePath.map((attribute, attributeIndex) => (
                              <Badge key={attributeIndex} bg="light" text="dark" className="border">
                                {attribute}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          <div className="d-flex flex-wrap gap-1">
                            {memberSkus.length === 0 ? (
                              <span className="text-muted small">No SKUs</span>
                            ) : (
                              <>
                                {memberSkus.slice(0, 3).map((sku, skuIndex) => (
                                  <span key={`${sku}-${skuIndex}`} onClick={() => copySkuToClipboard(sku)}>
                                    <IdBadge type={IdBadgeType.SKU} value={sku} size="sm" showCopyIcon />
                                  </span>
                                ))}
                                {memberSkus.length > 3 && (
                                  <span
                                    className="text-muted small fst-italic"
                                    title={memberSkus.slice(3).join(', ')}
                                  >
                                    +{memberSkus.length - 3} more
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '8px 6px', fontSize: '0.8rem' }}>{convertToLocalTime(group.updatedAt)}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            title="Edit"
                            onClick={() => navigate(ROUTES.catalog.productGroups.edit(group.groupId))}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Delete"
                            onClick={() => deleteGroup(group)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                          );
                        })}
                        {paddingBottom > 0 && (
                          <tr style={{ height: paddingBottom }}><td colSpan={5} /></tr>
                        )}
                      </>
                    );
                  })()}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
